import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
} from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";

import { flattenStyles, platformProps } from "../../lib";
import { composeRefs } from "../slot";
import type {
  DropdownMenuCheckboxItemPrimitiveProps,
  DropdownMenuContentPrimitiveProps,
  DropdownMenuGroupPrimitiveProps,
  DropdownMenuItemIndicatorPrimitiveProps,
  DropdownMenuItemPrimitiveProps,
  DropdownMenuLabelPrimitiveProps,
  DropdownMenuPortalPrimitiveProps,
  DropdownMenuRadioGroupPrimitiveProps,
  DropdownMenuRadioItemPrimitiveProps,
  DropdownMenuRootPrimitiveProps,
  DropdownMenuSeparatorPrimitiveProps,
  DropdownMenuSubContentPrimitiveProps,
  DropdownMenuSubPrimitiveProps,
  DropdownMenuSubTriggerPrimitiveProps,
  DropdownMenuTriggerPrimitiveProps,
} from "./types";

type TabDirection = "next" | "previous";

/**
 * What the menu's parts need from Root: `close`, through Root's
 * `onOpenChange` (the path Escape takes); the trigger, which a Tab moves focus
 * on from; and the direction of the Tab that closed the menu, which any other
 * way of closing it (from the menu or a sub-menu) forgets.
 */
const DropdownMenuRootContext = createContext<{
  close: () => void;
  triggerRef: MutableRefObject<HTMLButtonElement | null>;
  tabbedRef: MutableRefObject<TabDirection | null>;
}>({ close: () => undefined, triggerRef: { current: null }, tabbedRef: { current: null } });

const TABBABLE =
  'a[href], button, input, select, textarea, summary, [contenteditable="true"], [tabindex]';

/** The scope's controls in Tab order (positive tabindex aside), skipping disabled and hidden ones. */
const tabbables = (scope: Element) =>
  Array.from(scope.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (element) =>
      element.tabIndex >= 0 &&
      !element.matches(":disabled") &&
      !element.closest("[inert]") &&
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility === "visible",
  );

/**
 * The control a Tab (or Shift+Tab) from the trigger reaches. In a dialog,
 * which keeps focus inside, it wraps round; elsewhere there may be none.
 */
function controlFrom(trigger: HTMLElement, direction: TabDirection) {
  const dialog = trigger.closest('[role="dialog"], [role="alertdialog"]');
  const controls = tabbables(dialog ?? trigger.ownerDocument.body);
  const index = controls.indexOf(trigger);
  if (index < 0) return undefined;
  const step = direction === "next" ? 1 : -1;
  const target = dialog
    ? controls[(index + step + controls.length) % controls.length]
    : controls[index + step];
  return target === trigger ? undefined : target;
}

// Root holds the open state rather than Radix, so Content can close the menu
// on Tab (`closeOnTab`). It keeps Radix's rules: controlled when `open` is
// set; uncontrolled, `onOpenChange` reports a change once it has rendered.
const DropdownMenuRoot = ({
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  ...props
}: DropdownMenuRootPrimitiveProps) => {
  const isControlled = openProp !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = isControlled ? openProp : uncontrolledOpen;

  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  });
  const reportedOpen = useRef(uncontrolledOpen);
  useEffect(() => {
    if (isControlled || reportedOpen.current === uncontrolledOpen) return;
    reportedOpen.current = uncontrolledOpen;
    onOpenChangeRef.current?.(uncontrolledOpen);
  }, [isControlled, uncontrolledOpen]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      else if (next !== openProp) onOpenChange?.(next);
    },
    [isControlled, openProp, onOpenChange],
  );
  const close = useCallback(() => handleOpenChange(false), [handleOpenChange]);
  // The Trigger's element, typed as its ref is (with `asChild`, the child's)
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const tabbedRef = useRef<TabDirection | null>(null);
  const context = useMemo(() => ({ close, triggerRef, tabbedRef }), [close]);

  return (
    <DropdownMenuRootContext.Provider value={context}>
      <DropdownMenuPrimitive.Root open={open} onOpenChange={handleOpenChange} {...props}>
        {children}
      </DropdownMenuPrimitive.Root>
    </DropdownMenuRootContext.Provider>
  );
};

DropdownMenuRoot.displayName = "DropdownMenuRoot";

const DropdownMenuTrigger = forwardRef<HTMLButtonElement, DropdownMenuTriggerPrimitiveProps>(
  (
    { children, disabled, asChild = false, className, testID, accessibilityLabel, ...props },
    ref,
  ) => {
    const { triggerRef } = useContext(DropdownMenuRootContext);
    return (
      <DropdownMenuPrimitive.Trigger
        ref={composeRefs(ref, triggerRef)}
        disabled={disabled}
        asChild={asChild}
        // Only when set: with asChild an undefined aria-label reaches the child's
        // props and can override the label it renders itself
        {...(accessibilityLabel !== undefined && { "aria-label": accessibilityLabel })}
        className={className}
        {...platformProps({ testID, slot: "dropdown-trigger" })}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Trigger>
    );
  },
);

DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

const DropdownMenuPortal = ({
  children,
  container,
  forceMount,
}: DropdownMenuPortalPrimitiveProps) => (
  <DropdownMenuPrimitive.Portal
    container={container ?? undefined}
    forceMount={forceMount as true | undefined}
  >
    {children}
  </DropdownMenuPrimitive.Portal>
);

DropdownMenuPortal.displayName = "DropdownMenuPortal";

/**
 * The shared `style` can be a React Native style array. Through
 * DropdownMenuPortal, Radix clones Content with its Slot, whose style merge
 * (`{ ...slot, ...child }`) spreads an array into an object keyed "0", "1", …;
 * take its values back as the list before flattening (nothing is lost).
 */
function contentStyle(style: DropdownMenuContentPrimitiveProps["style"]) {
  const keys = style && !Array.isArray(style) ? Object.keys(style) : [];
  const spread = keys.length > 0 && keys.every((key) => /^\d+$/.test(key));
  return flattenStyles([spread ? Object.values(style as object) : style]);
}

const DropdownMenuContent = forwardRef<HTMLDivElement, DropdownMenuContentPrimitiveProps>(
  (
    {
      side = "bottom",
      align = "start",
      sideOffset = 4,
      alignOffset = 0,
      scrollHeight = 0,
      forceMount,
      closeOnTab = false,
      className,
      style,
      // Native only: web animates with classes
      enterFadeMs: _enterFadeMs,
      children,
      testID,
      accessibilityLabel,
      ...props
    },
    ref,
  ) => {
    const { close, triggerRef, tabbedRef } = useContext(DropdownMenuRootContext);
    // A Tab or Shift+Tab closed the menu: focus moves on from the trigger once
    // it has. Any other way of closing it forgets that, so a menu the screen
    // kept open on Tab doesn't move focus on when it closes later
    const forgetTab = () => {
      tabbedRef.current = null;
    };
    // Runs before Radix's own handler, which holds Tab in the menu. A Tab in
    // a sub-menu arrives here too, already default-prevented by it
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (!closeOnTab) return;
      if (event.key !== "Tab") return forgetTab();
      event.preventDefault();
      tabbedRef.current = event.shiftKey ? "previous" : "next";
      close();
    };
    // Radix focuses the trigger on close; after a Tab, the control beyond it
    // takes focus instead. With none (the page's last control), or one that
    // won't take focus, the trigger keeps it
    const onCloseAutoFocus = (event: Event) => {
      props.onCloseAutoFocus?.(event);
      const direction = tabbedRef.current;
      forgetTab();
      const trigger = triggerRef.current;
      if (!direction || event.defaultPrevented || !trigger) return;
      const target = controlFrom(trigger, direction);
      if (!target) return;
      event.preventDefault();
      target.focus();
      if (trigger.ownerDocument.activeElement !== target) trigger.focus();
    };
    return (
      <DropdownMenuPrimitive.Content
        ref={ref}
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        forceMount={forceMount as true | undefined}
        aria-label={accessibilityLabel}
        // Radix names the menu by its trigger (aria-labelledby), which wins over
        // aria-label: a given label takes its place
        {...(accessibilityLabel ? { "aria-labelledby": undefined } : {})}
        className={className}
        style={contentStyle(style)}
        {...platformProps({ testID, slot: "dropdown-content" })}
        {...props}
        onKeyDown={onKeyDown}
        onCloseAutoFocus={onCloseAutoFocus}
        onPointerDownCapture={forgetTab}
        onEscapeKeyDown={(event) => {
          forgetTab();
          props.onEscapeKeyDown?.(event);
        }}
        onPointerDownOutside={(event) => {
          forgetTab();
          props.onPointerDownOutside?.(event);
        }}
        onFocusOutside={(event) => {
          forgetTab();
          props.onFocusOutside?.(event);
        }}
      >
        {scrollHeight > 0 ? (
          <div style={{ maxHeight: scrollHeight, overflowY: "auto" }}>{children}</div>
        ) : (
          children
        )}
      </DropdownMenuPrimitive.Content>
    );
  },
);

DropdownMenuContent.displayName = "DropdownMenuContent";

const DropdownMenuItem = forwardRef<HTMLDivElement, DropdownMenuItemPrimitiveProps>(
  (
    {
      onSelect,
      closeOnSelect = true,
      disabled,
      className,
      children,
      testID,
      accessibilityLabel,
      ...props
    },
    ref,
  ) => (
    <DropdownMenuPrimitive.Item
      ref={ref}
      disabled={disabled}
      onSelect={(event) => {
        if (!closeOnSelect) event.preventDefault();
        onSelect?.();
      }}
      aria-label={accessibilityLabel}
      className={className}
      {...platformProps({ testID, slot: "dropdown-item" })}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.Item>
  ),
);

DropdownMenuItem.displayName = "DropdownMenuItem";

const DropdownMenuCheckboxItem = forwardRef<HTMLDivElement, DropdownMenuCheckboxItemPrimitiveProps>(
  (
    {
      checked,
      onCheckedChange,
      closeOnSelect = true,
      disabled,
      className,
      children,
      testID,
      accessibilityLabel,
      ...props
    },
    ref,
  ) => (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      checked={checked}
      onCheckedChange={onCheckedChange}
      onSelect={(event) => {
        if (!closeOnSelect) event.preventDefault();
      }}
      disabled={disabled}
      aria-label={accessibilityLabel}
      className={className}
      {...platformProps({ testID, slot: "dropdown-checkbox-item" })}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  ),
);

DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

const DropdownMenuRadioGroup = forwardRef<HTMLDivElement, DropdownMenuRadioGroupPrimitiveProps>(
  ({ value, onValueChange, className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.RadioGroup
      ref={ref}
      value={value}
      onValueChange={onValueChange}
      className={className}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.RadioGroup>
  ),
);

DropdownMenuRadioGroup.displayName = "DropdownMenuRadioGroup";

const DropdownMenuRadioItem = forwardRef<HTMLDivElement, DropdownMenuRadioItemPrimitiveProps>(
  (
    {
      value,
      closeOnSelect = true,
      disabled,
      className,
      children,
      testID,
      accessibilityLabel,
      ...props
    },
    ref,
  ) => (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      value={value}
      disabled={disabled}
      onSelect={(event) => {
        if (!closeOnSelect) event.preventDefault();
      }}
      aria-label={accessibilityLabel}
      className={className}
      {...platformProps({ testID, slot: "dropdown-radio-item" })}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.RadioItem>
  ),
);

DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";

const DropdownMenuItemIndicator = forwardRef<
  HTMLDivElement,
  DropdownMenuItemIndicatorPrimitiveProps
>(({ forceMount, className, children }, ref) => (
  <DropdownMenuPrimitive.ItemIndicator
    ref={ref}
    forceMount={forceMount as true | undefined}
    className={className}
  >
    {children}
  </DropdownMenuPrimitive.ItemIndicator>
));

DropdownMenuItemIndicator.displayName = "DropdownMenuItemIndicator";

const DropdownMenuSeparator = forwardRef<HTMLDivElement, DropdownMenuSeparatorPrimitiveProps>(
  ({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Separator ref={ref} className={className} {...props} />
  ),
);

DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

const DropdownMenuLabel = forwardRef<HTMLDivElement, DropdownMenuLabelPrimitiveProps>(
  ({ className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.Label ref={ref} className={className} {...props}>
      {children}
    </DropdownMenuPrimitive.Label>
  ),
);

DropdownMenuLabel.displayName = "DropdownMenuLabel";

const DropdownMenuGroup = forwardRef<HTMLDivElement, DropdownMenuGroupPrimitiveProps>(
  ({ className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.Group ref={ref} className={className} {...props}>
      {children}
    </DropdownMenuPrimitive.Group>
  ),
);

DropdownMenuGroup.displayName = "DropdownMenuGroup";

const DropdownMenuSub = ({ children, ...props }: DropdownMenuSubPrimitiveProps) => (
  <DropdownMenuPrimitive.Sub {...props}>{children}</DropdownMenuPrimitive.Sub>
);

DropdownMenuSub.displayName = "DropdownMenuSub";

const DropdownMenuSubTrigger = forwardRef<HTMLDivElement, DropdownMenuSubTriggerPrimitiveProps>(
  ({ disabled, className, children, testID, accessibilityLabel, ...props }, ref) => (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      disabled={disabled}
      aria-label={accessibilityLabel}
      className={className}
      {...platformProps({ testID, slot: "dropdown-sub-trigger" })}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.SubTrigger>
  ),
);

DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

const DropdownMenuSubContent = forwardRef<HTMLDivElement, DropdownMenuSubContentPrimitiveProps>(
  ({ sideOffset = 4, alignOffset = 0, forceMount, className, children, ...props }, ref) => {
    const { tabbedRef } = useContext(DropdownMenuRootContext);
    // A sub-menu's Escape or outside press closes the whole menu without
    // reaching Content's handlers: it forgets a Tab the screen refused too
    const forgetTab = () => {
      tabbedRef.current = null;
    };
    return (
      <DropdownMenuPrimitive.SubContent
        ref={ref}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        forceMount={forceMount as true | undefined}
        className={className}
        onEscapeKeyDown={forgetTab}
        onPointerDownOutside={forgetTab}
        onFocusOutside={forgetTab}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.SubContent>
    );
  },
);

DropdownMenuSubContent.displayName = "DropdownMenuSubContent";

export {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuItemIndicator,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
